import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Dumbbell } from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  ImageZoomDialog,
  cn,
} from "@cliently/ui";
import { useTranslation } from "react-i18next";
import { parseYoutubeVideoId, youtubeEmbedUrl } from "../../lib/youtube";

type ExerciseInfo = {
  name: string;
  nameFr: string | null;
  images: string[];
  youtubeUrl: string | null;
  instructions: string[];
  instructionsFr: string[];
  primaryMuscles: string[];
  category: string | null;
  level: string | null;
  equipment: string | null;
};

interface ExerciseGuideDialogProps {
  exercise: ExerciseInfo | null;
  open: boolean;
  onClose: () => void;
}



export const ExerciseGuideDialog = ({
  exercise,
  open,
  onClose,
}: ExerciseGuideDialogProps) => {
  const { t, i18n } = useTranslation();
  const [slideIdx, setSlideIdx] = useState<number>(0);
  const [zoomOpen, setZoomOpen] = useState(false);

  const displayName =
    i18n.language === "fr" && exercise?.nameFr
      ? exercise.nameFr
      : exercise?.name ?? "";

  const instructions = useMemo(() => {
    if (!exercise) return [];
    if (i18n.language === "fr" && exercise.instructionsFr.length > 0)
      return exercise.instructionsFr;
    return exercise.instructions;
  }, [exercise, i18n.language]);

  const videoId = useMemo(
    () => parseYoutubeVideoId(exercise?.youtubeUrl),
    [exercise?.youtubeUrl],
  );
  const hasVideo = !!videoId;
  const imageCount = exercise?.images.length ?? 0;
  const totalSlides = (hasVideo ? 1 : 0) + imageCount;

  const isVideoSlide = hasVideo && slideIdx === 0;
  const imageIndex = hasVideo ? slideIdx - 1 : slideIdx;

  if (!exercise) return null;

  const currentImageSrc =
    !isVideoSlide && imageCount > 0 ? exercise.images[imageIndex] : null;

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl p-0">
        <div className="relative isolate flex min-h-0 flex-1 flex-col overflow-y-auto bg-background">
          {totalSlides > 0 ? (
            <div className="relative z-0 aspect-video w-full shrink-0 overflow-hidden bg-muted">
              {isVideoSlide && videoId ? (
                <iframe
                  title={displayName}
                  src={youtubeEmbedUrl(videoId)}
                  className="h-full w-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : (
                <button
                  type="button"
                  className="h-full w-full cursor-zoom-in"
                  onClick={() => setZoomOpen(true)}
                  aria-label={t(
                    "exerciseGuide.zoomImage",
                    "Agrandir l'image de l'exercice",
                  )}
                >
                  <img
                    src={exercise.images[imageIndex]}
                    alt={`${displayName} — ${imageIndex + 1}`}
                    className="h-full w-full object-contain"
                  />
                </button>
              )}
              {totalSlides > 1 && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setSlideIdx((i) => (i === 0 ? totalSlides - 1 : i - 1))
                    }
                    className="absolute left-2 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-background/80 p-1.5 shadow-none backdrop-blur-sm hover:bg-background"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setSlideIdx((i) => (i === totalSlides - 1 ? 0 : i + 1))
                    }
                    className="absolute right-2 top-1/2 h-auto w-auto -translate-y-1/2 rounded-full bg-background/80 p-1.5 shadow-none backdrop-blur-sm hover:bg-background"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                  <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
                    {Array.from({ length: totalSlides }, (_, i) => (
                      <Button
                        key={i}
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setSlideIdx(i)}
                        className={cn(
                          "h-1.5 min-h-0 w-1.5 min-w-0 rounded-full p-0 shadow-none transition-colors hover:bg-transparent",
                          i === slideIdx
                            ? "bg-primary hover:bg-primary"
                            : "bg-background/60 hover:bg-background/60",
                        )}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : null}

          <div className="space-y-4 p-5">
            <div className="flex items-center gap-2">
              <Dumbbell className="text-primary h-5 w-5 shrink-0" />
              <h2 className="text-lg font-bold leading-tight">{displayName}</h2>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {exercise.category && (
                <Badge variant="outline">{exercise.category}</Badge>
              )}
              {exercise.level && (
                <Badge variant="secondary">{exercise.level}</Badge>
              )}
              {exercise.equipment && (
                <Badge variant="secondary">{exercise.equipment}</Badge>
              )}
            </div>

            {exercise.primaryMuscles.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-1.5">
                  {t("exerciseGuide.muscles", "Muscles ciblés")}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {exercise.primaryMuscles.map((m) => (
                    <Badge
                      key={m}
                      className="bg-primary/10 text-primary border-primary/20"
                    >
                      {m}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {instructions.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2">
                  {t("exerciseGuide.howTo", "Comment réaliser l'exercice")}
                </h3>
                <ol className="space-y-2.5">
                  {instructions.map((step, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                        {idx + 1}
                      </span>
                      <p className="text-sm leading-relaxed pt-0.5">{step}</p>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {currentImageSrc ? (
      <ImageZoomDialog
        open={zoomOpen}
        onOpenChange={setZoomOpen}
        src={currentImageSrc}
        alt={`${displayName} — ${imageIndex + 1}`}
      />
    ) : null}
    </>
  );
}
